// Audits page look: two columns on white, desktop first. Class names are
// written out in full so Tailwind can see them. Soft violet only for hover and
// the website matches; strong purple (violet-600) only for small active
// controls. Titles are small grey capitals.

export const AUDITS = {
  page: "h-full flex bg-white",
  // Left: what's coming (Your area, What you have). Right: the Snapshots list.
  side: "w-[34%] max-w-[30rem] shrink-0 border-r border-slate-200 overflow-y-auto px-7 py-6 space-y-8",
  main: "flex-1 min-w-0 overflow-y-auto px-7 py-6",
  title: "text-[0.6875rem] font-semibold text-slate-500 uppercase tracking-wider",
  titleRow: "flex items-baseline justify-between gap-3 mb-3",
  titleCount: "text-xs text-slate-400",

  // Placeholders: quiet, no numbers.
  placeholder: "border border-dashed border-slate-200 rounded-lg px-4 py-5 text-sm text-slate-400",

  search: "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-violet-400 bg-white",

  // Website matches, above the list.
  website: "mt-3 border border-violet-100 bg-violet-50/60 rounded-lg px-4 py-3",
  websiteHead: "text-xs text-slate-500",
  websiteDomain: "font-semibold text-slate-800",
  websiteRow: "w-full flex items-center justify-between gap-4 text-left text-sm px-2 py-1.5 -mx-2 rounded-md hover:bg-violet-100",
  websiteName: "font-medium text-slate-800 truncate",
  websiteDate: "text-xs text-slate-400 shrink-0",

  // Niche groups.
  groups: "mt-5 space-y-5",
  groupHead: "w-full flex items-center gap-2 py-1 text-left group",
  chevron: "w-3 text-[0.625rem] text-slate-400 group-hover:text-slate-600",
  groupName: "text-[0.6875rem] font-semibold text-slate-500 uppercase tracking-wider group-hover:text-slate-800",
  groupCount: "text-xs text-slate-400",
  rows: "mt-1 border-t border-slate-100",

  // One snapshot.
  row: "flex items-start gap-4 border-b border-slate-100 hover:bg-violet-50/40",
  // Running progress (Industry Audits page only).
  rowBar: "h-1 rounded-sm bg-[#F1EEFB] mt-1.5 max-w-[14rem] overflow-hidden",
  rowBarFill: "h-1 bg-violet-500",
  rowOpen: "flex-1 min-w-0 text-left px-2 py-2",
  rowTop: "flex items-baseline justify-between gap-4",
  rowName: "text-sm font-semibold text-slate-900 truncate",
  rowDate: "text-xs text-slate-400 shrink-0",
  rowMeta: "flex items-center flex-wrap gap-x-2.5 gap-y-1 mt-0.5",
  rowTitle: "text-[0.8125rem] text-slate-600",
  rowId: "text-xs text-slate-400",
  tag: "text-[0.6875rem] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded",
  // Stop, for a running snapshot: small, sits at the row's right.
  stop: "shrink-0 self-center mr-2 text-xs font-medium text-slate-600 border border-slate-300 rounded-md px-2 py-1 hover:border-red-300 hover:text-red-700 disabled:opacity-50",

  message: "py-16 text-center text-sm text-slate-400",
  // Empty account: the one strong purple button.
  startButton: "mt-4 inline-block text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg px-4 py-2",
};

// The Industry Audits layout (accounts with the switch on), from the approved
// mockup (docs/specs/audits-page-mockup.dc.html). Desktop (lg and up): two
// columns, 5 : 7, filling the window, lists scrolling inside their panels.
// Narrower: the columns stack and the page scrolls.
export const INDUSTRY = {
  page: "h-full flex flex-col bg-white",
  body: "flex-1 min-h-0 overflow-y-auto lg:overflow-hidden px-6 pt-[1.125rem] pb-6 flex flex-col gap-[1.125rem] lg:grid lg:grid-cols-[5fr_7fr] lg:grid-rows-[minmax(0,1fr)]",
  column: "min-w-0 flex flex-col gap-[1.125rem] lg:min-h-0 lg:overflow-y-auto",
  panel: "border border-slate-200 rounded-xl bg-white",
  label: "text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500",
  note: "text-[0.8125rem] text-slate-500",
  warning: "text-[0.8125rem] text-amber-700",

  // Industry bar: tabs, More and New search.
  bar: "relative flex items-center justify-between flex-wrap gap-3 px-6 border-b border-slate-200 bg-white",
  tabs: "flex items-stretch gap-x-6 flex-wrap min-w-0",
  tab: "flex items-center gap-2 pt-3.5 pb-3 text-[0.9375rem] font-semibold text-slate-600 border-b-2 border-transparent hover:text-slate-900",
  tabActive: "flex items-center gap-2 pt-3.5 pb-3 text-[0.9375rem] font-semibold text-slate-900 border-b-2 border-violet-600",
  tabCount: "text-xs font-medium text-slate-500",
  more: "pt-3.5 pb-3 text-sm font-medium text-slate-600 border-b-2 border-transparent hover:text-slate-900",
  moreMenu: "absolute top-full mt-1 z-20 w-56 bg-white border border-slate-200 rounded-[0.625rem] shadow-[0_8px_24px_rgba(15,23,42,0.1)] p-1.5",
  moreItem: "w-full flex justify-between gap-3 px-2.5 py-2 text-sm text-slate-900 rounded-md hover:bg-violet-50 text-left",
  moreCount: "text-xs text-slate-500",
  start: "my-2 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg px-4 py-2",

  // Industry Audit panel.
  // Desktop: the left column splits 3 : 2 like the right one, so AdA lines up
  // with Advertisers; this panel scrolls inside if its contents don't fit.
  audit: "px-5 py-[1.125rem] flex flex-col gap-4 lg:flex-[3_1_0%] lg:min-h-0 lg:overflow-y-auto",
  auditHead: "flex items-start justify-between gap-3",
  auditName: "mt-1 text-2xl leading-tight font-bold text-slate-900",
  auditSince: "mt-0.5 text-[0.8125rem] text-slate-600",
  pillBuilding: "shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-violet-100 text-violet-800",
  pillLock: "w-3.5 h-3.5",
  pillUnlocked: "shrink-0 text-xs font-semibold px-3 py-1 rounded-full bg-violet-800 text-white",
  ringsRow: "flex flex-wrap items-center gap-5",
  rings: "w-[9.375rem] h-[9.375rem] shrink-0",
  stats: "flex-1 basis-[13rem] min-w-0 grid grid-cols-2 gap-x-4 gap-y-3",
  statLabel: "flex items-center gap-1.5 text-xs text-slate-600",
  statValue: "text-[1.5625rem] leading-tight font-bold text-slate-900 tabular-nums",
  // "679 / 1,000": the target after the number, smaller and grey, then a tick
  // (purple once met, grey until then).
  statValueRow: "flex items-center gap-2",
  statTarget: "text-sm font-semibold text-slate-400",
  tickMet: "w-[1.125rem] h-[1.125rem] shrink-0 text-violet-600",
  tickNotMet: "w-[1.125rem] h-[1.125rem] shrink-0 text-slate-300",
  statSub: "text-xs text-slate-500",
  graphs: "flex flex-wrap gap-5",
  graph: "flex-[3_1_12rem] min-w-0",
  mix: "flex-[2_1_10rem] min-w-0",
  graphSvg: "w-full h-16 block mt-1.5",
  mixBar: "flex h-3 rounded-md overflow-hidden gap-0.5 mt-2",
  mixLegend: "grid grid-cols-2 gap-x-2.5 gap-y-1 mt-2",
  mixItem: "flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap",
  nudgeRow: "flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-slate-200",
  nudge: "text-sm font-semibold text-slate-900",
  // View Industry Report: purple once unlocked, grey with a padlock until then.
  report: "shrink-0 inline-flex items-center gap-1.5 text-[0.8125rem] font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg px-3 py-1.5",
  reportLocked: "shrink-0 inline-flex items-center gap-1.5 text-[0.8125rem] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-lg px-3 py-1.5 cursor-not-allowed",
  reportLock: "w-3.5 h-3.5",

  // Ring and graph colours (SVG): outer = searches, middle = locations, inner = keywords.
  ringTrack: "fill-none stroke-[#F1EEFB]",
  ringFill: ["fill-none stroke-violet-800", "fill-none stroke-violet-500", "fill-none stroke-violet-300"],
  swatch: ["bg-violet-800", "bg-violet-500", "bg-violet-300", "bg-slate-200"],
  graphArea: "fill-[#F1EEFB]",
  graphLine: "fill-none stroke-violet-500",

  // AdA panel.
  ada: "px-5 py-[1.125rem] flex flex-col gap-3 lg:flex-[2_1_0%] lg:min-h-0 lg:overflow-y-auto",
  // The niche line scrolls across the whole row and slides behind the logo
  // (white, on top). Animation: .ada-ticker in src/index.css.
  adaHead: "relative flex items-center",
  // The strip's width: wider shows more of the text beside the logo.
  adaTicker: "absolute inset-y-0 left-0 w-[26rem] max-w-full flex items-center overflow-hidden",
  adaLogo: "relative z-10 flex items-center gap-2.5 bg-white pr-3",
  adaWordmark: "h-[1.0625rem] w-auto block",
  adaOn: "text-base font-medium text-slate-700",
  adaLine: "text-sm text-slate-800",

  // Market Audits and Snapshots panel.
  // Desktop: Market and Advertisers share the right column 3 : 2.
  market: "overflow-hidden flex flex-col lg:flex-[3_1_0%] lg:min-h-0",
  strip: "grid grid-cols-2 sm:grid-cols-4 border-b border-slate-200 bg-violet-50/40",
  stripTile: "px-[1.125rem] py-3 min-w-0",
  stripValue: "text-[1.375rem] font-bold text-slate-900 tabular-nums whitespace-nowrap",
  stripValueRow: "flex items-center gap-2 mt-0.5",
  live: "relative flex w-2.5 h-2.5",
  livePing: "absolute inline-flex w-full h-full rounded-full bg-violet-400 opacity-60 animate-ping motion-reduce:animate-none",
  liveDot: "relative inline-flex w-2.5 h-2.5 rounded-full bg-violet-600",
  marketBody: "flex flex-wrap lg:flex-nowrap lg:flex-1 lg:min-h-0",
  // List and map share the box half and half (Scott, 4 Nov 2026).
  marketList: "flex-[1_1_16rem] min-w-0 flex flex-col px-[1.125rem] pt-3.5 pb-2 lg:min-h-0",
  marketHead: "flex items-baseline justify-between gap-2.5 mb-2",
  marketRows: "mt-2 border-t border-slate-100 overflow-y-auto max-h-[22rem] lg:max-h-none lg:flex-1 lg:min-h-0",
  coverage: "flex-[1_1_16rem] min-w-0 px-[1.125rem] py-3.5 border-t sm:border-t-0 sm:border-l border-slate-200 flex flex-col gap-2 lg:min-h-0",
  coverageHead: "flex items-center justify-between gap-3",
  // The same street map as the New Search page (SearchMap).
  map: "h-64 lg:h-auto lg:flex-1 lg:min-h-[10rem] rounded-lg overflow-hidden border border-slate-200",
  legends: "flex items-center gap-3",
  legend: "flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap",
  legendRepeated: "inline-block w-2.5 h-2.5 rounded-full bg-violet-600 border border-violet-700",
  legendOneOff: "inline-block w-2.5 h-2.5 rounded-full bg-violet-200 border border-violet-500",

  // Coverage pins (Leaflet needs real colour values, match the legend above).
  // Repeated = searched on 2+ days: strong purple; One-off: pale. Hovering a
  // Snapshot in the list lights up its pins, rings its main postcode and
  // fades the rest.
  // opacity is set on every look so a faded pin comes back in full.
  pin: {
    repeated: { radius: 6, color: "#6d28d9", weight: 1.5, fillColor: "#7c3aed", opacity: 1, fillOpacity: 1 },
    oneOff: { radius: 5, color: "#8b5cf6", weight: 1.5, fillColor: "#ddd6fe", opacity: 1, fillOpacity: 1 },
    lit: { radius: 7, color: "#5b21b6", weight: 2, fillColor: "#7c3aed", opacity: 1, fillOpacity: 1 },
    faded: { opacity: 0.3, fillOpacity: 0.25 },
    mainRing: { radius: 12, color: "#6d28d9", weight: 2, fillOpacity: 0 },
  },

  // Advertisers panel: 2 parts of the right column to Market's 3 (desktop), scrolls inside.
  advertisers: "px-[1.125rem] pt-3.5 pb-3 flex flex-col gap-2.5 lg:flex-[2_1_0%] lg:min-h-0 lg:overflow-y-auto",
};
